import React, { useEffect, useState } from "react";
import Logo from "./images/logo.png";
import { ChevronLeft, Menu, X, Zap } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGameContext } from "./GameContext";
import ExitGameModal from "./ExitGameModal";
import DocumentalBanner from "./DocumentalBanner";
import BirthdayBanner from "./BirthdayBanner";
import AnniversaryBanner from "./AnniversaryBanner";
import { useAuth } from "./AuthContext";

const isInStandaloneMode = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const TopNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/home";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [exitTarget, setExitTarget] = useState(null);
  const { isPlaying, score } = useGameContext();
  const [isInstalled, setIsInstalled] = useState(false);
  const { user, isLoggedIn } = useAuth();

  const shouldBlock = isPlaying && score > 0;

  useEffect(() => {
    setIsInstalled(isInStandaloneMode());
  }, []);

  // Infiere a dónde regresar según la ruta actual
  const getBackRoute = () => {
    const path = location.pathname;
    // Estas siempre van a /home sin importar de dónde vengan
    if (path === "/generate-tweet" || path === "/tendencia") return "/home";
    if (path === "/quotes") return "/home";
    if (path === "/about") return "/home";
    if (path === "/feedback") return "/home";
    if (path === "/feedback/bug" || path === "/feedback/sugerencia")
      return "/feedback";
    if (path === "/add-phrase") return "/home";
    if (path === "/como-agregar") return "/home";
    if (path === "/select-game") return "/home";
    if (path === "/login") return "/home";
    if (path === "/perfil") return "/home";
    if (path === "/migrar") return "/perfil";
    if (path === "/leaderboard") return "/home";

    // Para el juego y todo lo demás, usa el historial real
    return -1;
  };

  const handleNavigate = (target) => {
    if (shouldBlock) {
      setExitTarget(target);
    } else {
      navigate(target);
    }
  };

  const confirmExit = () => {
    if (exitTarget !== null) {
      navigate(exitTarget);
      setExitTarget(null);
    }
  };

  const cancelExit = () => setExitTarget(null);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const navigateToAddPhrase = () => {
    handleNavigate("/add-phrase");
    closeMenu();
  };
  const navigateToAbout = () => {
    handleNavigate("/about");
    closeMenu();
  };
  const navigateToFeedback = () => {
    handleNavigate("/feedback");
    closeMenu();
  };
  const navigateToAddToHomePage = () => {
    handleNavigate("/como-agregar");
    closeMenu();
  };

  return (
    <>
      <nav className="w-full bg-white border-b border-gray-200 px-4 py-4 relative z-20 font-quicksand">
        <div className="flex items-center justify-between">
          {isHomePage ? (
            <button onClick={toggleMenu} className="p-2 rounded-full">
              {isMenuOpen ? (
                <X strokeWidth={2.25} />
              ) : (
                <Menu strokeWidth={2.25} />
              )}
            </button>
          ) : (
            <button
              onClick={() => handleNavigate(getBackRoute())}
              className="p-2 rounded-full"
            >
              <ChevronLeft strokeWidth={2.25} />
            </button>
          )}

          <button
            onClick={() => handleNavigate("/home")}
            className="flex items-center"
          >
            <img src={Logo} alt="logo" className="h-8" />
          </button>

          <button
            onClick={() => {
              if (isLoggedIn) {
                handleNavigate("/perfil");
              } else {
                handleNavigate("/login");
              }
            }}
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: isLoggedIn
                ? "linear-gradient(135deg, #b06ab3, #c4b5fd)"
                : "#f0e8f8",
              border: isLoggedIn ? "none" : "1.5px solid #c4b5fd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {isLoggedIn && user ? (
              <span
                style={{ fontSize: "13px", fontWeight: 800, color: "white" }}
              >
                {user.username[0].toUpperCase()}
              </span>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#b06ab3"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Banners — solo en homepage */}
      {isHomePage && <BirthdayBanner />}
      {isHomePage && <DocumentalBanner />}
      {isHomePage && <AnniversaryBanner />}

      {/* Sliding Menu */}
      <div
        className={`fixed top-0 left-0 h-full w-80 z-10 transition-transform duration-300 ease-in-out ${
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ background: "#F2F6FF" }}
      >
        <div className="pt-24 px-6 py-4 relative h-full">
          <nav className="space-y-4">
            <button
              onClick={navigateToAddPhrase}
              className="w-full text-left px-4 py-3 rounded-xl font-medium text-base active:scale-95 transition-all"
              style={{
                background: "rgba(255,255,255,0.85)",
                border: "1.5px solid #c4b5fd",
                color: "#2d1f3d",
              }}
            >
              ✨ ¿Falta una frase icónica?
            </button>
            <button
              onClick={navigateToAbout}
              className="w-full text-left px-4 py-3 rounded-xl font-medium text-base active:scale-95 transition-all"
              style={{
                background: "rgba(255,255,255,0.85)",
                border: "1.5px solid #c4b5fd",
                color: "#2d1f3d",
              }}
            >
              ✨ Sobre esta app
            </button>
            <button
              onClick={navigateToFeedback}
              className="w-full text-left px-4 py-3 rounded-xl font-medium text-base active:scale-95 transition-all"
              style={{
                background: "rgba(255,255,255,0.85)",
                border: "1.5px solid #c4b5fd",
                color: "#2d1f3d",
              }}
            >
              ✨ Feedback o errorcito
            </button>
            {!isInstalled && (
              <button
                onClick={navigateToAddToHomePage}
                className="w-full text-left px-4 py-3 rounded-xl font-medium text-base active:scale-95 transition-all"
                style={{
                  background: "rgba(255,255,255,0.85)",
                  border: "1.5px solid #c4b5fd",
                  color: "#2d1f3d",
                }}
              >
                ✨ Agrega esta app a tu pantalla
              </button>
            )}
          </nav>

          <div className="absolute bottom-4 left-0 right-0 px-6 text-xs text-gray-400 flex items-center justify-center">
            <Zap size={16} className="text-gray-600 mr-1" fill="currentColor" />
            Powered by Mely
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          style={{ zIndex: 5 }}
          onClick={closeMenu}
        />
      )}

      <ExitGameModal
        isOpen={exitTarget !== null}
        onConfirm={confirmExit}
        onCancel={cancelExit}
      />
    </>
  );
};

export default TopNav;
